import { FC, useState, ChangeEvent, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import Papa from 'papaparse'
import styles from 'styles/components/template.module.scss'
import DataLoad from 'components/Init/DataLoad'
import PageHedding from 'components/PageHedding/PageHedding'
import FileUploadButton from 'components/Button/FileUploadButton'
import SubmitButton from 'components/Button/SubmitButton'
import InputTextSuggestWithLightLabel from 'components/Form/WithLightLabel/InputTextSuggest'
import InputText from 'components/Form/InputText'
import SelectBox from 'components/Form/SelectBox'
import CheckBox from 'components/Form/CheckBox'
import { suggestInfo } from 'components/Form/InputTextSuggest'
import clearCrossIcon from 'images/Form/clear-cross-icon.png'
import BulkUploadConfirmModal from 'components/Modal/BulkUploadConfirmModal'
import { setIsLoading } from 'reducks/loading/slice'
import {
  createMaterialCostsBulk,
  SupplierType,
} from 'reducks/materialCost/slice'
import { getMaterialById, MaterialType } from 'reducks/material/slice'
import { getWholesalers, WholesalerType } from 'reducks/wholesaler/slice'
import { RootState } from 'reducks/reducers'
import { Role } from 'reducks/staff/slice'
import { DataType } from 'reducks/business/slice'
import { httpsCallable } from 'firebase/functions'
import { functions, db } from 'fb/index'
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore'

// 定数定義
const MAX_CSV_ROWS = 300
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
// 更新用フォーマット（問屋コードあり）
const EXPECTED_CSV_HEADERS_UPDATE = [
  '問屋コード',
  '問屋名',
  '商品コード',
  '商品名',
  'メーカー',
  '内容量',
  '単位',
  'バラ単価（税抜）',
  '仕入税率',
]

// 新規登録用フォーマット（問屋コードなし）
const EXPECTED_CSV_HEADERS_NEW = [
  '問屋名',
  '商品コード',
  '商品名',
  'メーカー',
  '内容量',
  '単位',
  'バラ単価（税抜）',
  '仕入税率',
]

interface CsvRow {
  wholesalerId: string | null
  wholesalerCode: string
  wholesalerName: string
  productCode: string
  productName: string
  manufacturer: string
  internalCapacityCount: string
  internalCapacityUnit: string
  costPrice: string
  tax: string
  materialId: string | null
  materialName: string
  rowIndex: number
}

interface DiffItem {
  type: 'new' | 'update' | 'skip'
  row: CsvRow
  usedCosting: boolean
  changes?: {
    field: string
    oldValue: string
    newValue: string
  }[]
}

interface DiffSummary {
  newCount: number
  updateCount: number
  skipCount: number
  items: DiffItem[]
}

const MaterialCostBulkUploadTemplate: FC = () => {
  const dispatch = useDispatch<any>()
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  const { staff, adminShopId } = useSelector(
    (state: RootState) => state.staffSlice,
  )

  const [csvRows, setCsvRows] = useState<CsvRow[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<
    Map<number, Map<string, string>>
  >(new Map())
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [diffSummary, setDiffSummary] = useState<DiffSummary | null>(null)
  const [useAsCosting, setUseAsCosting] = useState(true) // デフォルトtrue
  // 既存の問屋ID+商品コード → 原材料IDのマップ
  const [existingCombinationMap, setExistingCombinationMap] = useState<
    Map<string, string>
  >(new Map())

  // 権限チェック
  const checkPermission = useCallback(() => {
    if (!staff) {
      navigate('/initload?path=' + path)
      window.scrollTo(0, 0)
      return false
    }

    // 原価一覧メニューの表示ロジックに準拠
    const hasPermission =
      (staff.isStandardPlan &&
        staff.isAdmin === false &&
        (!staff.ds_roles ||
          staff.ds_roles?.filter(
            (role: Role) => role.store_ref.id === staff.storeId,
          ).length === 0 ||
          staff.ds_roles?.filter(
            (role: Role) => role.store_ref.id === staff.storeId,
          )[0].role === 9)) ||
      (staff.isAdmin === true && adminShopId)

    if (!hasPermission) {
      navigate('/initload')
      window.scrollTo(0, 0)
      return false
    }

    return true
  }, [staff, adminShopId, navigate, path])

  useEffect(() => {
    checkPermission()
  }, [checkPermission])

  // 単位を全角から半角に正規化する関数
  const normalizeUnit = (unit: string): string => {
    if (!unit) return unit
    // 全角文字を半角に変換
    let normalized = unit
      .replace(/ｇ/g, 'g')
      .replace(/ｋ/g, 'k')
      .replace(/Ｌ/g, 'L')
      .replace(/ｍ/g, 'm')
      .replace(/Ｍ/g, 'M')

    // 小文字のlをLに統一
    normalized = normalized.replace(/ml/gi, 'mL').replace(/^l$/i, 'L')

    return normalized
  }

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (!file.name.endsWith('.csv')) {
      setUploadError('CSVファイルを選択してください')
      return
    }

    // ファイルサイズチェック
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('ファイルサイズは5MB以下にしてください')
      return
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: 'UTF-8',
      complete: async (results) => {
        try {
          // ヘッダー検証（新規登録用・更新用の両フォーマットに対応）
          const headers = results.meta.fields || []
          const isUpdateFormat = EXPECTED_CSV_HEADERS_UPDATE.every(
            (expected: string, index: number) => headers[index] === expected,
          )
          const isNewFormat = EXPECTED_CSV_HEADERS_NEW.every(
            (expected: string, index: number) => headers[index] === expected,
          )

          if (!isUpdateFormat && !isNewFormat) {
            setUploadError(
              'CSVファイルのヘッダーが正しくありません。フォーマットをご確認ください',
            )
            return
          }

          if (results.data.length === 0) {
            setUploadError('CSVファイルにデータが含まれていません')
            return
          }

          // 最大行数チェック
          if (results.data.length > MAX_CSV_ROWS) {
            alert(
              `CSVファイルに${results.data.length}行のデータが含まれていますが、最大${MAX_CSV_ROWS}行まで処理されます`,
            )
          }

          // データ行をパース
          const rows: CsvRow[] = results.data
            .slice(0, MAX_CSV_ROWS)
            .map((row: any, index: number) => {
              const rawUnit = (row['単位'] || '').trim()
              const normalizedUnit = normalizeUnit(rawUnit)
              console.log(
                `CSV行 ${
                  index + 1
                }: 元の単位="${rawUnit}", 正規化後="${normalizedUnit}"`,
              )
              return {
                wholesalerId: null,
                wholesalerCode: (row['問屋コード'] || '').trim(),
                wholesalerName: (row['問屋名'] || '').trim(),
                productCode: (row['商品コード'] || '').trim(),
                productName: (row['商品名'] || '').trim(),
                manufacturer: (row['メーカー'] || '').trim(),
                internalCapacityCount: (row['内容量'] || '').trim(),
                internalCapacityUnit: normalizedUnit,
                costPrice: (row['バラ単価（税抜）'] || '').trim(),
                tax: (row['仕入税率'] || '').trim(),
                materialId: null,
                materialName: '',
                rowIndex: index + 1,
              }
            })

          // 既存のmaterial_costsを取得して照合
          if (staff?.shopId) {
            try {
              // 問屋マスタを取得
              const wholesalersPayload = await dispatch(
                getWholesalers({ includeInactive: true }),
              )
              const wholesalers: WholesalerType[] =
                wholesalersPayload.payload || []

              // 問屋コードをキーとしたMapを作成
              const wholesalerMap = new Map<string, WholesalerType>()
              wholesalers.forEach((wholesaler) => {
                if (wholesaler.id) {
                  wholesalerMap.set(wholesaler.id, wholesaler)
                }
              })

              const materialCostsQuery = query(
                collection(db, 'material_costs'),
                where('shop_ref', '==', doc(db, 'shops', staff.shopId)),
              )
              const materialCostsSnapshot = await getDocs(materialCostsQuery)

              // サプライヤー索引をMapで作成（パフォーマンス最適化）
              const supplierIndex = new Map<
                string,
                { materialId: string; materialRef: any }
              >()
              // 既存の問屋ID+商品コード → 原材料IDのマップ
              const combinationMap = new Map<string, string>()

              materialCostsSnapshot.docs.forEach((docSnapshot) => {
                const data = docSnapshot.data()
                const suppliers = data.suppliers || []
                const materialRef = data.material_ref

                suppliers.forEach((supplier: SupplierType) => {
                  if (supplier.wholesaler_ref && supplier.product_code) {
                    const key = `${supplier.wholesaler_ref.id}_${supplier.product_code}`
                    supplierIndex.set(key, {
                      materialId: materialRef.id,
                      materialRef,
                    })
                    // 既存組み合わせマップに追加
                    combinationMap.set(key, materialRef.id)
                  }
                })
              })

              // 既存組み合わせマップをstateに保存
              setExistingCombinationMap(combinationMap)

              const updatedRows = await Promise.all(
                rows.map(async (row) => {
                  let updatedRow = { ...row }

                  // 問屋コードがある場合、問屋情報を自動選択
                  if (row.wholesalerCode) {
                    const wholesaler = wholesalerMap.get(row.wholesalerCode)
                    if (wholesaler) {
                      updatedRow.wholesalerId = wholesaler.id
                      updatedRow.wholesalerName = wholesaler.name
                    }
                  }

                  // 問屋コードと商品コードの両方が入力されている場合
                  if (row.wholesalerCode && row.productCode) {
                    const key = `${row.wholesalerCode}_${row.productCode}`
                    const match = supplierIndex.get(key)

                    if (match) {
                      // 原材料情報を取得して自動選択（商品名・メーカーは上書きしない）
                      const materialInfo = await dispatch(
                        getMaterialById({ id: match.materialId }),
                      )
                      if (materialInfo && materialInfo.payload) {
                        const material: MaterialType = materialInfo.payload

                        updatedRow = {
                          ...updatedRow,
                          materialId: material.id,
                          materialName: material.product_name,
                          // productNameとmanufacturerはCSV値を保持
                        }
                      }
                    }
                  }
                  return updatedRow
                }),
              )

              setCsvRows(updatedRows)
            } catch (error) {
              console.error('既存データの照合に失敗しました:', error)
              setCsvRows(rows)
            }
          } else {
            setCsvRows(rows)
          }

          setUploadError(null)
          setValidationErrors(new Map())
        } catch (error) {
          setUploadError('CSVファイルの読み込みに失敗しました')
          console.error('CSV読み込みエラー:', error)
        }
      },
      error: (error) => {
        setUploadError(`CSVファイルの解析に失敗しました: ${error.message}`)
        console.error('CSVパースエラー:', error)
      },
    })

    event.target.value = ''
  }

  const handleMaterialNameChange = (index: number, value: string) => {
    const newRows = [...csvRows]
    newRows[index].materialName = value
    newRows[index].materialId = null
    setCsvRows(newRows)
  }

  const handleMaterialSelect = async (index: number, info: suggestInfo) => {
    const row = csvRows[index]

    // 既存の組み合わせチェック（リアルタイムバリデーション）
    if (row.wholesalerId && row.productCode) {
      const key = `${row.wholesalerId}_${row.productCode}`
      const existingMaterialId = existingCombinationMap.get(key)

      if (existingMaterialId && existingMaterialId !== info.id) {
        // 既に別の原材料に紐付いている場合、原材料名を取得してアラート
        try {
          const existingMaterialInfo = await dispatch(
            getMaterialById({ id: existingMaterialId }),
          )
          const existingMaterialName =
            existingMaterialInfo?.payload?.product_name || '不明な原材料'

          alert(
            `この問屋コード（${row.wholesalerCode}）と商品コード（${row.productCode}）の組み合わせは、既に原材料「${existingMaterialName}」に登録されています。\n\n別の原材料には登録できません。変更・削除は原材料原価情報画面から操作してください。`,
          )

          // 選択をクリア
          const newRows = [...csvRows]
          newRows[index].materialId = null
          newRows[index].materialName = ''
          setCsvRows(newRows)
          return // 選択を中断
        } catch (error) {
          console.error('既存原材料の情報取得に失敗しました:', error)
        }
      }
    }

    // 通常の選択処理（商品名・メーカーは上書きしない）
    const materialInfo = await dispatch(getMaterialById({ id: info.id }))
    if (materialInfo && materialInfo.payload) {
      const material: MaterialType = materialInfo.payload

      const newRows = [...csvRows]
      newRows[index].materialId = material.id
      newRows[index].materialName = material.product_name
      // productNameとmanufacturerはCSV値を保持（上書きしない）
      setCsvRows(newRows)
    }
  }

  const handleClearMaterial = (index: number) => {
    const newRows = [...csvRows]
    newRows[index].materialId = null
    newRows[index].materialName = ''
    setCsvRows(newRows)
  }

  const handleWholesalerChange = (index: number, value: string) => {
    const newRows = [...csvRows]
    newRows[index].wholesalerName = value
    newRows[index].wholesalerId = null
    setCsvRows(newRows)
  }

  const handleWholesalerSelect = (index: number, info: suggestInfo) => {
    const newRows = [...csvRows]
    newRows[index].wholesalerId = info.id
    newRows[index].wholesalerCode = info.id
    newRows[index].wholesalerName = info.name
    setCsvRows(newRows)
  }

  const handleClearWholesaler = (index: number) => {
    const newRows = [...csvRows]
    newRows[index].wholesalerId = null
    newRows[index].wholesalerCode = ''
    newRows[index].wholesalerName = ''
    setCsvRows(newRows)
  }

  const handleRowChange = (
    index: number,
    field: keyof CsvRow,
    value: string,
  ) => {
    const newRows = [...csvRows]
    // 型安全に各フィールドを更新
    if (
      field === 'wholesalerCode' ||
      field === 'wholesalerName' ||
      field === 'productCode' ||
      field === 'productName' ||
      field === 'manufacturer' ||
      field === 'internalCapacityCount' ||
      field === 'internalCapacityUnit' ||
      field === 'costPrice' ||
      field === 'tax' ||
      field === 'materialName'
    ) {
      newRows[index][field] = value
    }
    setCsvRows(newRows)
  }

  const validation = async (): Promise<boolean> => {
    if (csvRows.length === 0) {
      alert('CSVファイルをアップロードしてください')
      return false
    }

    const errors = new Map<number, Map<string, string>>()
    const validUnits = ['g', 'kg', 'mL', 'L']

    // CSV内での重複チェック用Map
    const csvUniqueKeys = new Map<string, number>()

    for (let i = 0; i < csvRows.length; i++) {
      const row = csvRows[i]
      const fieldErrors = new Map<string, string>()

      if (!row.materialId) {
        fieldErrors.set('materialName', '原材料を選択してください')
      }

      // 問屋選択チェック
      if (!row.wholesalerId) {
        fieldErrors.set('wholesalerName', '問屋を選択してください')
      }

      // 商品コードの入力チェック
      if (!row.productCode || !row.productCode.trim()) {
        fieldErrors.set('productCode', '商品コードを入力してください')
      }

      // 一意性チェック: CSV内での重複
      if (row.wholesalerCode && row.productCode) {
        const uniqueKey = `${row.wholesalerCode}_${row.productCode}`
        if (csvUniqueKeys.has(uniqueKey)) {
          const duplicateRow = csvUniqueKeys.get(uniqueKey)! + 1
          fieldErrors.set('productCode', `${duplicateRow}行目と重複しています`)
        } else {
          csvUniqueKeys.set(uniqueKey, i)
        }
      }

      if (!row.costPrice || isNaN(Number(row.costPrice))) {
        fieldErrors.set('costPrice', 'バラ単価が正しくありません')
      } else if (Number(row.costPrice) <= 0) {
        fieldErrors.set('costPrice', '0より大きい値を入力してください')
      }

      if (!row.tax || isNaN(Number(row.tax))) {
        fieldErrors.set('tax', '仕入税率が正しくありません')
      } else if (Number(row.tax) < 0 || Number(row.tax) > 100) {
        fieldErrors.set('tax', '0〜100の範囲で入力してください')
      }

      if (
        !row.internalCapacityCount ||
        isNaN(Number(row.internalCapacityCount))
      ) {
        fieldErrors.set('internalCapacityCount', '内容量が正しくありません')
      } else if (Number(row.internalCapacityCount) <= 0) {
        fieldErrors.set(
          'internalCapacityCount',
          '0より大きい値を入力してください',
        )
      }

      if (
        !validUnits.includes(row.internalCapacityUnit) ||
        row.internalCapacityUnit === '----'
      ) {
        fieldErrors.set('internalCapacityUnit', '単位を選択してください')
      }

      if (fieldErrors.size > 0) {
        errors.set(i, fieldErrors)
      }
    }

    // 既存データとの重複チェック（問屋コード+商品コードの組み合わせを完全に禁止）
    if (staff?.shopId && errors.size === 0) {
      try {
        const materialCostsQuery = query(
          collection(db, 'material_costs'),
          where('shop_ref', '==', doc(db, 'shops', staff.shopId)),
        )
        const materialCostsSnapshot = await getDocs(materialCostsQuery)

        // 既存の問屋ID + 商品コード → 原材料IDのマップを作成
        const existingKeyToMaterialMap = new Map<string, string>()
        materialCostsSnapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data()
          const suppliers = data.suppliers || []
          const materialRef = data.material_ref

          suppliers.forEach((supplier: SupplierType) => {
            if (supplier.wholesaler_ref && supplier.product_code) {
              const key = `${supplier.wholesaler_ref.id}_${supplier.product_code}`
              existingKeyToMaterialMap.set(key, materialRef.id)
            }
          })
        })

        // CSVの各行をチェック
        for (let i = 0; i < csvRows.length; i++) {
          const row = csvRows[i]
          // wholesalerIdを使用（CSVから取得した問屋マスタのFirebase ID）
          if (row.wholesalerId && row.productCode) {
            const uniqueKey = `${row.wholesalerId}_${row.productCode}`
            const existingMaterialId = existingKeyToMaterialMap.get(uniqueKey)

            // 既存の組み合わせがある場合
            if (existingMaterialId) {
              // 別の原材料に付け替えようとしている場合のみエラー
              if (row.materialId !== existingMaterialId) {
                const fieldErrors = errors.get(i) || new Map<string, string>()
                fieldErrors.set(
                  'materialName',
                  'この問屋コード・商品コードは既に別の原材料に登録されています',
                )
                errors.set(i, fieldErrors)
              }
              // 同じ原材料なら更新OK（エラーなし）
            }
          }
        }
      } catch (error) {
        console.error('既存データチェックエラー:', error)
      }
    }

    setValidationErrors(errors)

    if (errors.size > 0) {
      const firstErrorRow = Array.from(errors.keys())[0]
      const firstRowErrors = errors.get(firstErrorRow)!
      const errorMessages = Array.from(firstRowErrors.values()).join(', ')
      alert(`${firstErrorRow + 1}行目: ${errorMessages}`)
      window.scrollTo(0, 0)
      return false
    }

    return true
  }

  // 差分を検出する関数
  const detectDiff = async (): Promise<DiffSummary> => {
    const items: DiffItem[] = []
    let newCount = 0
    let updateCount = 0
    let skipCount = 0

    if (!staff?.shopId) {
      return {
        newCount: csvRows.length,
        updateCount: 0,
        skipCount: 0,
        items: [],
      }
    }

    try {
      // 既存の原価情報を取得
      const materialCostsQuery = query(
        collection(db, 'material_costs'),
        where('shop_ref', '==', doc(db, 'shops', staff.shopId)),
      )
      const materialCostsSnapshot = await getDocs(materialCostsQuery)

      // 原材料ごとの既存原価情報の有無を確認
      const existingMaterialsMap = new Map<string, boolean>()
      materialCostsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data()
        const materialId = data.material_ref?.id
        if (materialId) {
          existingMaterialsMap.set(materialId, true)
        }
      })

      // 既存データをMapに格納（問屋ID + 商品コードをキーに）
      const existingMap = new Map<string, SupplierType>()
      materialCostsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data()
        const suppliers = data.suppliers || []
        suppliers.forEach((supplier: SupplierType) => {
          if (supplier.wholesaler_ref && supplier.product_code) {
            const key = `${supplier.wholesaler_ref.id}_${supplier.product_code}`
            existingMap.set(key, supplier)
          }
        })
      })

      // チェックON時: 原材料ごとにg単価が最安値のインデックスを記録
      const materialCheapestIndex = new Map<string, number>()
      if (useAsCosting) {
        csvRows.forEach((row, index) => {
          const materialId = row.materialId!
          const currentCheapest = materialCheapestIndex.get(materialId)

          // g単価を計算（単位に応じて換算）
          const getGramUnitPrice = (r: CsvRow) => {
            let unitAdjustment = 1
            if (r.internalCapacityUnit === 'kg') {
              unitAdjustment = 1000
            } else if (r.internalCapacityUnit === 'L') {
              unitAdjustment = 1000
            }
            return (
              Number(r.costPrice) /
              Number(r.internalCapacityCount) /
              unitAdjustment
            )
          }

          if (currentCheapest === undefined) {
            materialCheapestIndex.set(materialId, index)
          } else {
            const currentGramPrice = getGramUnitPrice(csvRows[currentCheapest])
            const newGramPrice = getGramUnitPrice(row)
            if (newGramPrice < currentGramPrice) {
              materialCheapestIndex.set(materialId, index)
            }
          }
        })
      }

      // 各行を判定（新規 or 更新 or スキップ）
      csvRows.forEach((row, index) => {
        // used_costingの判定
        let usedCosting: boolean
        if (useAsCosting) {
          // チェックON: 同じ原材料の中で最安値のみtrue
          const cheapestIndex = materialCheapestIndex.get(row.materialId!)
          usedCosting = cheapestIndex === index
        } else {
          // チェックOFF: 初回登録のみtrue
          const isFirstRegistration = !existingMaterialsMap.has(row.materialId!)
          usedCosting = isFirstRegistration
        }

        // 既存サプライヤーをチェック
        if (row.wholesalerId && row.productCode) {
          const key = `${row.wholesalerId}_${row.productCode}`
          const existingSupplier = existingMap.get(key)

          if (existingSupplier) {
            // 既存サプライヤーが見つかった場合、差分をチェック
            const changes: {
              field: string
              oldValue: string
              newValue: string
            }[] = []

            // バラ単価の比較
            if (existingSupplier.cost_price !== Number(row.costPrice)) {
              changes.push({
                field: 'バラ単価',
                oldValue: String(existingSupplier.cost_price),
                newValue: row.costPrice,
              })
            }

            // 仕入税率の比較
            if (existingSupplier.tax !== Number(row.tax)) {
              changes.push({
                field: '仕入税率',
                oldValue: String(existingSupplier.tax),
                newValue: row.tax,
              })
            }

            // 内容量の比較
            if (
              existingSupplier.internal_capacity_count !==
              Number(row.internalCapacityCount)
            ) {
              changes.push({
                field: '内容量',
                oldValue: String(existingSupplier.internal_capacity_count),
                newValue: row.internalCapacityCount,
              })
            }

            // 単位の比較
            if (
              existingSupplier.internal_capacity_unit !==
              row.internalCapacityUnit
            ) {
              changes.push({
                field: '単位',
                oldValue: existingSupplier.internal_capacity_unit,
                newValue: row.internalCapacityUnit,
              })
            }

            if (changes.length > 0) {
              // 差分あり → 更新
              updateCount++
              items.push({ type: 'update', row, usedCosting, changes })
            } else {
              // 差分なし → スキップ
              skipCount++
              items.push({ type: 'skip', row, usedCosting })
            }
          } else {
            // 新規登録
            newCount++
            items.push({ type: 'new', row, usedCosting })
          }
        } else {
          // 問屋IDまたは商品コードがない → 新規登録として扱う
          newCount++
          items.push({ type: 'new', row, usedCosting })
        }
      })
    } catch (error) {
      console.error('差分検出エラー:', error)
    }

    return { newCount, updateCount, skipCount, items }
  }

  const handleSubmit = async () => {
    if (!(await validation())) {
      return
    }

    // 差分を検出してモーダル表示
    const diff = await detectDiff()

    // 新規・更新がない場合（すべてスキップ）は即座にアラート
    if (diff.newCount === 0 && diff.updateCount === 0) {
      alert(
        '登録する変更がありません。すべてのレコードが既存のデータと同じ内容です。',
      )
      return
    }

    setDiffSummary(diff)
    setShowConfirmModal(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false)
    dispatch(setIsLoading({ isLoading: true }))

    try {
      if (!staff?.shopId) {
        alert('店舗情報が取得できません')
        return
      }

      // 差分サマリーからスキップ以外の行を抽出
      if (!diffSummary) {
        alert('差分情報が取得できません')
        dispatch(setIsLoading({ isLoading: false }))
        return
      }

      const rowsToProcess = diffSummary.items
        .filter((item) => item.type !== 'skip')
        .map((item) => item.row)

      if (rowsToProcess.length === 0) {
        alert('登録する変更がありません')
        dispatch(setIsLoading({ isLoading: false }))
        return
      }

      // 既存の原価情報を取得
      const materialCostsQuery = query(
        collection(db, 'material_costs'),
        where('shop_ref', '==', doc(db, 'shops', staff.shopId)),
      )
      const materialCostsSnapshot = await getDocs(materialCostsQuery)

      // 原材料ごとの既存原価情報の有無を確認
      const existingMaterialsMap = new Map<string, boolean>()
      materialCostsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data()
        const materialId = data.material_ref?.id
        if (materialId) {
          existingMaterialsMap.set(materialId, true)
        }
      })

      // 登録する原価情報のmaterial_idリストを取得（スキップ以外）
      const registeringMaterialIds = new Set(
        rowsToProcess.map((row) => row.materialId!),
      )

      // チェックON時: 原材料ごとにg単価が最安値のインデックスを記録
      const materialCheapestIndex = new Map<string, number>()
      if (useAsCosting) {
        rowsToProcess.forEach((row, index) => {
          const materialId = row.materialId!
          const currentCheapest = materialCheapestIndex.get(materialId)

          // g単価を計算（単位に応じて換算）
          const getGramUnitPrice = (r: CsvRow) => {
            let unitAdjustment = 1
            if (r.internalCapacityUnit === 'kg') {
              unitAdjustment = 1000
            } else if (r.internalCapacityUnit === 'L') {
              unitAdjustment = 1000
            }
            return (
              Number(r.costPrice) /
              Number(r.internalCapacityCount) /
              unitAdjustment
            )
          }

          if (currentCheapest === undefined) {
            materialCheapestIndex.set(materialId, index)
          } else {
            const currentGramPrice = getGramUnitPrice(
              rowsToProcess[currentCheapest],
            )
            const newGramPrice = getGramUnitPrice(row)
            if (newGramPrice < currentGramPrice) {
              materialCheapestIndex.set(materialId, index)
            }
          }
        })
      }

      // 各行のused_costingを決定（スキップ以外のみ処理）
      const materialCosts = rowsToProcess.map((row, index) => {
        let usedCosting: boolean

        if (useAsCosting) {
          // チェックON: 同じ原材料の中で最安値のみtrue
          const cheapestIndex = materialCheapestIndex.get(row.materialId!)
          usedCosting = cheapestIndex === index
        } else {
          // チェックOFF: 初回登録のみtrue
          const isFirstRegistration = !existingMaterialsMap.has(row.materialId!)
          usedCosting = isFirstRegistration
        }

        const supplier: SupplierType = {
          internal_capacity: null,
          internal_capacity_manual_input: null,
          internal_capacity_count: Number(row.internalCapacityCount),
          internal_capacity_unit: row.internalCapacityUnit,
          number_item: 1,
          number_item_unit: '個',
          supplier_category: 'wholesale',
          supplier_company_name: row.wholesalerName,
          supplier_branch_name: '',
          wholesaler_ref: row.wholesalerId
            ? doc(db, 'wholesalers', row.wholesalerId)
            : undefined,
          product_code: row.productCode,
          cost_price: Number(row.costPrice),
          tax: Number(row.tax),
          memo: '',
          used_costing: usedCosting,
          cost_price_history: null,
          gram_per_piece: null,
          gram_per_piece_unit: null,
        }

        return {
          material_id: row.materialId!,
          suppliers: [supplier],
          yield_rate: 100,
          edible_part: 0,
          in_edible_part: 0,
          author: '',
        }
      })

      const result = await dispatch(createMaterialCostsBulk({ materialCosts }))

      // チェックONの場合、同じ原材料の既存原価情報のused_costingをfalseに更新
      if (useAsCosting && result.payload?.successCount > 0) {
        for (const materialId of registeringMaterialIds) {
          // 該当原材料のmaterial_costドキュメントを取得
          const materialCostQuery = query(
            collection(db, 'material_costs'),
            where('shop_ref', '==', doc(db, 'shops', staff.shopId)),
            where('material_ref', '==', doc(db, 'materials', materialId)),
          )
          const materialCostDocs = await getDocs(materialCostQuery)

          for (const docSnapshot of materialCostDocs.docs) {
            const data = docSnapshot.data()
            const suppliers = data.suppliers || []

            // 今回登録した原価情報以外のused_costingをfalseに
            const updatedSuppliers = suppliers.map((supplier: SupplierType) => {
              // 今回登録した原価情報かどうかを判定
              const isNewlyRegistered = csvRows.some(
                (row) =>
                  row.materialId === materialId &&
                  row.wholesalerId === supplier.wholesaler_ref?.id &&
                  row.productCode === supplier.product_code,
              )

              if (!isNewlyRegistered) {
                return { ...supplier, used_costing: false }
              }
              return supplier
            })

            // Firestoreを更新
            await updateDoc(doc(db, 'material_costs', docSnapshot.id), {
              suppliers: updatedSuppliers,
            })
          }
        }
      }

      // 登録した原材料を使用している商品の原価を再計算する。
      // これを呼ばないと依存商品の cost_price / not_cost_calculated_reasons が
      // 古いまま残り、原価一覧で「未登録」アラートが消えない。
      // 単品編集(MaterialCostOperationTemplate)と同様に原材料単位でトリガーする。
      if (result.payload?.successCount > 0 && registeringMaterialIds.size > 0) {
        const updateBackSealStatusAndCostDaseruno = httpsCallable(
          functions,
          'updateBackSealStatusAndCostDaseruno',
        )
        await Promise.all(
          Array.from(registeringMaterialIds).map((materialId) =>
            updateBackSealStatusAndCostDaseruno({
              docId: materialId,
              dataType: DataType.MATERIAL,
              shopId: staff.shopId,
            }),
          ),
        )
      }

      if (result.payload) {
        const { successCount, errorCount, errors } = result.payload
        if (errorCount > 0) {
          alert(
            `登録完了: ${successCount}件成功, ${errorCount}件失敗\n\nエラー:\n${errors.join(
              '\n',
            )}`,
          )
        } else {
          alert(`${successCount}件の原価情報を登録しました`)
        }
      }

      navigate('/material')
    } catch (error) {
      console.error('一括登録エラー:', error)
      const errorMessage =
        error instanceof Error
          ? `登録に失敗しました: ${error.message}`
          : '登録に失敗しました。もう一度お試しください。'
      alert(errorMessage)
    } finally {
      dispatch(setIsLoading({ isLoading: false }))
    }
  }

  // 新規登録用フォーマットをダウンロード
  const downloadNewTemplate = () => {
    const headers = [
      '問屋名',
      '商品コード',
      '商品名',
      'メーカー',
      '内容量',
      '単位',
      'バラ単価（税抜）',
      '仕入税率',
    ]
    const sampleRow = [
      'sacri商店',
      '20001',
      'インスタントDイースト赤',
      'sacri',
      '500',
      'g',
      '800',
      '8',
    ]

    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n')
    const bom = '\uFEFF'
    const blob = new Blob([bom + csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', '原価情報_新規登録用.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // 更新用フォーマット（既存データ）をダウンロード
  const downloadExistingData = async () => {
    if (!staff?.shopId) {
      alert('店舗情報が取得できません')
      return
    }

    try {
      dispatch(setIsLoading({ isLoading: true }))

      // 店舗内の全原価情報を取得
      const materialCostsQuery = query(
        collection(db, 'material_costs'),
        where('shop_ref', '==', doc(db, 'shops', staff.shopId)),
      )
      const materialCostsSnapshot = await getDocs(materialCostsQuery)

      if (materialCostsSnapshot.empty) {
        alert('登録されている原価情報がありません')
        return
      }

      const headers = [
        '問屋コード',
        '問屋名',
        '商品コード',
        '商品名',
        'メーカー',
        '内容量',
        '単位',
        'バラ単価（税抜）',
        '仕入税率',
      ]

      const rows: string[] = [headers.join(',')]

      // 各原材料の原価情報をCSV行に変換
      for (const docSnapshot of materialCostsSnapshot.docs) {
        const data = docSnapshot.data()
        const suppliers = data.suppliers || []
        const materialRef = data.material_ref

        // 原材料情報を取得
        let materialName = ''
        let manufacturer = ''
        if (materialRef) {
          try {
            const materialInfo = await dispatch(
              getMaterialById({ id: materialRef.id }),
            )
            if (materialInfo && materialInfo.payload) {
              const material: MaterialType = materialInfo.payload
              materialName = material.product_name || ''
              manufacturer = material.manufacturer || ''
            }
          } catch (error) {
            console.error('原材料情報取得エラー:', error)
          }
        }

        for (const supplier of suppliers) {
          // 商品コードが空のレコードはスキップ
          if (!supplier.product_code || !supplier.product_code.trim()) {
            continue
          }

          // wholesaler_refから問屋IDを取得
          const wholesalerCode = supplier.wholesaler_ref
            ? supplier.wholesaler_ref.id
            : ''

          const row = [
            wholesalerCode,
            supplier.supplier_company_name || '',
            supplier.product_code || '',
            materialName,
            manufacturer,
            String(supplier.internal_capacity_count || ''),
            supplier.internal_capacity_unit || '',
            String(supplier.cost_price || ''),
            String(supplier.tax || ''),
          ]
          rows.push(row.join(','))
        }
      }

      const csvContent = rows.join('\n')
      const bom = '\uFEFF'
      const blob = new Blob([bom + csvContent], {
        type: 'text/csv;charset=utf-8;',
      })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute('download', '原価情報_更新用.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('CSV出力エラー:', error)
      alert('CSVの出力に失敗しました')
    } finally {
      dispatch(setIsLoading({ isLoading: false }))
    }
  }

  return (
    <div className={styles.container}>
      <DataLoad />
      <div className={styles.dashboardPageHeadContainer}>
        <PageHedding title="原価情報一括登録" to="/material" />
      </div>

      <div className={styles.pattern}>
        <div className={styles.pattern_head}>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              <button
                onClick={downloadNewTemplate}
                style={{
                  color: '#FF6B35',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                新規登録用フォーマットをダウンロード
              </button>
              <button
                onClick={downloadExistingData}
                style={{
                  color: '#FF6B35',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                更新用フォーマット（既存データ）をダウンロード
              </button>
            </div>
            <FileUploadButton
              label="CSVアップロード"
              description="最大300行まで処理されます"
              onChange={handleFileUpload}
            />
          </div>

          {uploadError && (
            <div style={{ color: 'red', marginTop: 16, fontSize: '14px' }}>
              {uploadError}
            </div>
          )}
        </div>

        {csvRows.length > 0 && (
          <>
            <div style={{ marginTop: 32 }}>
              <h3 style={{ marginBottom: 16, fontSize: '16px' }}>
                アップロード内容（{csvRows.length}件）
              </h3>
              <p
                style={{
                  marginBottom: 16,
                  color: '#666',
                  fontSize: '14px',
                }}
              >
                各行の内容を確認し、対応する原材料を選択してください
              </p>

              <div
                style={{
                  marginBottom: '50px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    maxHeight: '400px',
                    overflowX: 'auto',
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px',
                      minWidth: '1500px',
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '50px',
                          }}
                        >
                          行
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '250px',
                          }}
                        >
                          問屋選択
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '100px',
                          }}
                        >
                          問屋コード
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '200px',
                          }}
                        >
                          問屋名
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '100px',
                          }}
                        >
                          商品コード
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '200px',
                          }}
                        >
                          商品名
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '100px',
                          }}
                        >
                          メーカー
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '80px',
                          }}
                        >
                          内容量
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '60px',
                          }}
                        >
                          単位
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '100px',
                          }}
                        >
                          バラ単価
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '80px',
                          }}
                        >
                          税率(%)
                        </th>
                        <th
                          style={{
                            padding: '12px 8px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            minWidth: '250px',
                          }}
                        >
                          原材料選択（必須）
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ overflow: 'visible' }}>
                      {csvRows.map((row, index) => {
                        const rowErrors = validationErrors.get(index)
                        const hasError = rowErrors && rowErrors.size > 0
                        return (
                          <tr
                            key={index}
                            style={{
                              overflow: 'visible',
                            }}
                          >
                            <td
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                backgroundColor: '#fff',
                              }}
                            >
                              <div>{index + 1}</div>
                              {hasError && (
                                <div
                                  style={{
                                    color: 'red',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                  }}
                                  title={Array.from(rowErrors.values()).join(
                                    ', ',
                                  )}
                                >
                                  ⚠
                                </div>
                              )}
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                position: 'relative',
                                overflow: 'visible',
                                backgroundColor: rowErrors?.has(
                                  'wholesalerName',
                                )
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <div
                                style={{
                                  position: 'relative',
                                  overflow: 'visible',
                                }}
                              >
                                <InputTextSuggestWithLightLabel
                                  labelName=""
                                  description=""
                                  line={1}
                                  type="text"
                                  size="middle"
                                  value={row.wholesalerName}
                                  placeholder="問屋を選択"
                                  suggestType="wholesaler"
                                  index={index}
                                  onChange={(event) =>
                                    handleWholesalerChange(
                                      index,
                                      event.target.value,
                                    )
                                  }
                                  onSelect={(info) =>
                                    handleWholesalerSelect(index, info)
                                  }
                                  readOnly={row.wholesalerId ? true : false}
                                  style={{ paddingRight: 40 }}
                                />
                                {row.wholesalerId && (
                                  <div
                                    onClick={() => handleClearWholesaler(index)}
                                    style={{
                                      position: 'absolute',
                                      top: '50%',
                                      right: '10px',
                                      transform: 'translateY(-50%)',
                                      cursor: 'pointer',
                                      width: '20px',
                                      height: '20px',
                                    }}
                                  >
                                    <img
                                      src={clearCrossIcon}
                                      alt="クリア"
                                      style={{ width: '100%', height: '100%' }}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f9f9f9',
                                verticalAlign: 'middle',
                              }}
                            >
                              {row.wholesalerCode}
                            </td>
                            <td
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f9f9f9',
                                verticalAlign: 'middle',
                              }}
                            >
                              {row.wholesalerName}
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                backgroundColor: rowErrors?.has('productCode')
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <InputText
                                required={false}
                                type="text"
                                size="in_table"
                                value={row.productCode}
                                placeholder=""
                                rightAligned={false}
                                onChange={(e) =>
                                  handleRowChange(
                                    index,
                                    'productCode',
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f9f9f9',
                                verticalAlign: 'middle',
                              }}
                            >
                              {row.productName}
                            </td>
                            <td
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f9f9f9',
                                verticalAlign: 'middle',
                              }}
                            >
                              {row.manufacturer}
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                backgroundColor: rowErrors?.has(
                                  'internalCapacityCount',
                                )
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <InputText
                                required={false}
                                type="text"
                                size="in_table"
                                value={row.internalCapacityCount}
                                placeholder=""
                                rightAligned={true}
                                onChange={(e) =>
                                  handleRowChange(
                                    index,
                                    'internalCapacityCount',
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                backgroundColor: rowErrors?.has(
                                  'internalCapacityUnit',
                                )
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <SelectBox
                                style={{
                                  width: 90,
                                  height: 40,
                                  padding: '8px 12px',
                                }}
                                size="large2"
                                value={
                                  ['g', 'kg', 'mL', 'L'].includes(
                                    row.internalCapacityUnit,
                                  )
                                    ? row.internalCapacityUnit
                                    : '----'
                                }
                                options={[
                                  ...(!['g', 'kg', 'mL', 'L'].includes(
                                    row.internalCapacityUnit,
                                  )
                                    ? [
                                        {
                                          is_default: true,
                                          label: '----',
                                          value: '----',
                                        },
                                      ]
                                    : []),
                                  {
                                    is_default:
                                      row.internalCapacityUnit === 'g',
                                    label: 'g',
                                    value: 'g',
                                  },
                                  {
                                    is_default:
                                      row.internalCapacityUnit === 'kg',
                                    label: 'kg',
                                    value: 'kg',
                                  },
                                  {
                                    is_default:
                                      row.internalCapacityUnit === 'mL',
                                    label: 'mL',
                                    value: 'mL',
                                  },
                                  {
                                    is_default:
                                      row.internalCapacityUnit === 'L',
                                    label: 'L',
                                    value: 'L',
                                  },
                                ]}
                                onChange={(
                                  event: React.ChangeEvent<HTMLSelectElement>,
                                ) =>
                                  handleRowChange(
                                    index,
                                    'internalCapacityUnit',
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                backgroundColor: rowErrors?.has('costPrice')
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <InputText
                                required={false}
                                type="text"
                                size="in_table"
                                value={row.costPrice}
                                placeholder=""
                                rightAligned={true}
                                onChange={(e) =>
                                  handleRowChange(
                                    index,
                                    'costPrice',
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                backgroundColor: rowErrors?.has('tax')
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <InputText
                                required={false}
                                type="text"
                                size="in_table"
                                value={row.tax}
                                placeholder=""
                                rightAligned={true}
                                onChange={(e) =>
                                  handleRowChange(index, 'tax', e.target.value)
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                                verticalAlign: 'middle',
                                position: 'relative',
                                overflow: 'visible',
                                backgroundColor: rowErrors?.has('materialName')
                                  ? '#fff3cd'
                                  : '#fff',
                              }}
                            >
                              <div
                                style={{
                                  position: 'relative',
                                  overflow: 'visible',
                                }}
                              >
                                <InputTextSuggestWithLightLabel
                                  labelName=""
                                  description=""
                                  line={1}
                                  type="text"
                                  size="middle"
                                  value={row.materialName}
                                  placeholder="原材料名を入力"
                                  suggestType="material_product_name"
                                  index={index}
                                  onChange={(event) =>
                                    handleMaterialNameChange(
                                      index,
                                      event.target.value,
                                    )
                                  }
                                  onSelect={(info) =>
                                    handleMaterialSelect(index, info)
                                  }
                                  readOnly={row.materialId ? true : false}
                                  style={{ paddingRight: 40 }}
                                />
                                {row.materialId && (
                                  <div
                                    onClick={() => handleClearMaterial(index)}
                                    style={{
                                      position: 'absolute',
                                      top: '50%',
                                      right: '10px',
                                      transform: 'translateY(-50%)',
                                      cursor: 'pointer',
                                      width: '20px',
                                      height: '20px',
                                    }}
                                  >
                                    <img
                                      src={clearCrossIcon}
                                      alt="クリア"
                                      style={{ width: '100%', height: '100%' }}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <style>
                {`
                  .checkbox-wrapper label {
                    width: auto !important;
                  }
                `}
              </style>
              <div className="checkbox-wrapper">
                <CheckBox
                  isChecked={useAsCosting}
                  label="原価情報として利用する"
                  size="middle"
                  type="default"
                  onChange={() => setUseAsCosting(!useAsCosting)}
                />
              </div>
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '4px',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}
              >
                {useAsCosting ? (
                  <>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                      ✓ チェックON時の動作
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>登録する原価情報が原価計算に使用されます</li>
                      <li>
                        同じ原材料で複数の原価情報を登録する場合、
                        <strong>最安値の1件のみ</strong>が原価計算に使用されます
                      </li>
                      <li>同じ原材料の既存の原価情報は使用されなくなります</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                      ✓ チェックOFF時の動作
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>
                        原価情報として登録されますが、原価計算には使用されません
                      </li>
                      <li>
                        ただし、<strong>初めて登録する原材料</strong>
                        は自動的に原価計算に使用されます
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </div>

            <div className={styles.material_submit}>
              <SubmitButton
                label="キャンセル"
                color="orange_outlined"
                size="large"
                icon="none"
                onClick={() => navigate('/material')}
              />
              <span className={styles.material_submit_spacer}></span>
              <SubmitButton
                label="一括登録する"
                color="orange"
                size="large"
                icon="none"
                onClick={handleSubmit}
              />
            </div>
          </>
        )}
      </div>

      <BulkUploadConfirmModal
        isOpen={showConfirmModal}
        diffSummary={
          diffSummary || {
            newCount: 0,
            updateCount: 0,
            skipCount: 0,
            items: [],
          }
        }
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  )
}

export default MaterialCostBulkUploadTemplate
